import React, { useState } from 'react'
import { subscribe } from 'react-contextual'
import { Helmet } from 'react-helmet'
import { insert, pathOr, remove } from 'ramda'
import { useDispatch, useSelector } from 'react-redux'
import { format } from 'date-fns'
import { Button, DatePicker, DimmerLoader, Segment } from 'components'
import CalendarOff from '../../../../views/components/atoms/Svgs/CalendarOff'
import BulkImportInvoicesProvider from '../../state/BulkImportInvoicesProvider'
import TaskList from '../../../../views/components/atoms/Svgs/TaskList'
import FormErrors from '../../../../views/components/molecules/FormErrors/FormErrors'
import PaymentAllocation from './PaymentAllocation/PaymentAllocation'
import { partyApiSelectors } from '../../../api/party'
import { invoiceApiSelectors } from '../../../api/invoice'
import { Formik } from 'formik'
import styles from './BulkImportInvoiceView.module.scss'
import { useEffectOnce } from 'react-use'
import { hashCode } from 'utils/string'
import { bulkImportInvoicesEvents } from '../../state'

interface BulkImportInvoiceViewProps {
  closeInvoice: () => void
  invoice: {
    id: string
    customer: string
    customerName: string
    propertyAddress: string
    invoiceType: string
    dueDate: string
    vat: boolean
    customerTag: string
    portfolioId: string
    amount: number
    beneficiaries: Array<{
      beneficiary: {
        type: string
        value: {
          partyId: string
          partyTag: string
          name: string
          reference: string
          amount: number
          vat: boolean
        }
      }
    }>
  }
  invoiceViewId: string
}

const BulkImportInvoiceView = ({ closeInvoice, invoice, invoiceViewId }: BulkImportInvoiceViewProps) => {
  const dispatch = useDispatch()
  const [beneficiariesHash, setBeneficiariesHash] = useState(null)
  const getPartyNameById = useSelector(partyApiSelectors.getPartyNameById)
  const partyTags = useSelector(partyApiSelectors.getPartyTags)
  // `PaymentAllocation` requires an invoice type and reads `invoiceType.name` unguarded,
  // so a miss falls back to an empty one rather than the bare `''` it used to be handed.
  const invoiceType = useSelector(state => invoiceApiSelectors.getInvoiceTypeByValue(state)(invoice?.invoiceType)) || {
    name: '',
    value: '',
  }

  useEffectOnce(() => {
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
    setBeneficiariesHash(hashCode(JSON.stringify(pathOr('', ['beneficiaries'], invoice))))
  })

  return (
    <div>
      <Helmet>
        <title>{`reOS | Bulk Import Invoices | View ${invoice?.customerName}`}</title>
      </Helmet>
      <DimmerLoader className={styles.root} loading={!invoice} renderChildren={false}>
        <header className={styles.header}>
          <button className={styles.close} onClick={closeInvoice}>
            &nbsp;
          </button>
        </header>
        <div className={styles.body}>
          <Segment.Group>
            <Segment>
              <Segment.Label
                icon={<TaskList />}
                text={invoice?.customerName}
                secondaryText={invoice?.propertyAddress}
              />
            </Segment>
          </Segment.Group>

          <FormErrors errors={[]} />

          {invoice && (
            <Formik
              enableReinitialize
              initialValues={invoice}
              onSubmit={values => {
                // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
                dispatch(bulkImportInvoicesEvents.updateBulkInvoice(values, { invoiceViewId }))
              }}
            >
              {({ setFieldValue, handleChange, handleSubmit, values, dirty }) => {
                return (
                  <form onSubmit={handleSubmit}>
                    <PaymentAllocation
                      invoiceType={invoiceType}
                      amount={values.amount ? values.amount.toString() : ''}
                      amountVatApplied={values?.vat}
                      onAmountChange={(e: any) => setFieldValue('amount', e.target.value)}
                      onVatChange={(vat: any) => setFieldValue('vat', vat)}
                      onBeneficiaryVatChange={(index: number, vat: any) =>
                        setFieldValue(`beneficiaries[${index}].beneficiary.value.vat`, vat)
                      }
                      onDepositBeneficiaryTransferChange={console.log}
                      beneficiaries={values?.beneficiaries.map((b: any) => {
                        const partyId = pathOr('', ['beneficiary', 'value', 'partyId'], b)
                        return {
                          ...b,
                          beneficiary: {
                            ...b.beneficiary,
                            value: {
                              ...b.beneficiary.value,
                              name: getPartyNameById(partyId),
                            },
                          },
                        }
                      })}
                      onNewBeneficiaryAdded={(beneficiary: any, index: number) => {
                        setFieldValue('beneficiaries', insert(index, beneficiary, values.beneficiaries))
                      }}
                      onBeneficiaryRemoved={(index: number) => {
                        setFieldValue('beneficiaries', remove(index, 1, values.beneficiaries))
                      }}
                      onNewBeneficiaryAmountChange={(beneficiaryIndex: number, e: any) => {
                        const beneficiary = values.beneficiaries.find((v: any, i: any) => i === beneficiaryIndex)
                        setFieldValue(`beneficiaries[${beneficiaryIndex}]`, {
                          ...beneficiary,
                          beneficiary: {
                            ...beneficiary?.beneficiary,
                            value: {
                              ...beneficiary?.beneficiary.value,
                              amount: e.target.value ? e.target.value : 0,
                            },
                          },
                        })
                      }}
                      onBeneficiariesOrderChange={(newOrder: any) => {
                        const updatedBeneficiaries = newOrder.map((o: any) => values.beneficiaries[o])
                        setFieldValue('beneficiaries', updatedBeneficiaries)
                        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
                        setBeneficiariesHash(hashCode(JSON.stringify(updatedBeneficiaries)))
                      }}
                      partyTags={partyTags}
                    />

                    <Segment.Group>
                      <Segment horizontal>
                        <Segment.Label icon={<CalendarOff />} text="Payment Due" />
                        <div className={styles['due-date']}>
                          <DatePicker
                            date={values.dueDate ? new Date(values.dueDate) : new Date()}
                            onChange={(date: any) => setFieldValue('dueDate', format(date, 'yyyy-MM-dd'))}
                          />
                        </div>
                      </Segment>
                    </Segment.Group>

                    <div className={styles['form-footer']}>
                      <Button disabled={!dirty} type="submit">
                        Save
                      </Button>
                    </div>
                  </form>
                )
              }}
            </Formik>
          )}
        </div>
      </DimmerLoader>
    </div>
  )
}

export default subscribe([BulkImportInvoicesProvider], ({ closeInvoice, invoice, invoiceViewId }: any) => ({
  closeInvoice,
  invoice,
  invoiceViewId,
}))(BulkImportInvoiceView)
